import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { useProfiles } from '@/components/hooks/useProfiles';
import { toast } from 'sonner';
import { rutaInicioPanel } from '@/components/utils/rutasPanel';

const LS = {
  clientBookingReturnUrl: 'clientBooking_returnUrl',
  googleName: 'google_name',
  googleEmail: 'google_email',
  idUsuario: 'id_usuario',
  email: 'email',
  idPerfil: 'id_perfil',
  idEmpresa: 'id_empresa',
  afterRegisterReturnTo: 'after_register_return_to',
  oauthContext: 'oauth_context',
} as const;

function setGooglePrefill(user: any) {
  localStorage.setItem(
    LS.googleName,
    user.user_metadata?.full_name || user.user_metadata?.name || ''
  );
  localStorage.setItem(LS.googleEmail, user.email || '');
}

function clearProfileSelection() {
  localStorage.removeItem(LS.idPerfil);
  localStorage.removeItem(LS.idEmpresa);
}

function clearBookingReturn() {
  localStorage.removeItem(LS.clientBookingReturnUrl);
}

const OAuthCallback = () => {
  const navigate = useNavigate();
  const { hasPerfil, getPerfilesUsuario, getPerfilClienteDesactivado } =
    useProfiles();

  useEffect(() => {
    const run = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error || !session?.user) {
          if (error) Sentry.captureException(error);
          toast.error('Error al iniciar sesión');
          navigate('/', { replace: true });
          return;
        }

        const user = session.user;

        localStorage.setItem(LS.idUsuario, user.id);
        localStorage.setItem(LS.email, user.email || '');
        clearProfileSelection();

        Sentry.setUser({ id: user.id, email: user.email ?? undefined });

        const params = new URLSearchParams(window.location.search);
        const flow = params.get('flow');
        const intent = params.get('intent');
        const returnToParam = params.get('return_to');

        const isClientBooking =
          intent === 'clientBooking' ||
          localStorage.getItem(LS.clientBookingReturnUrl) !== null;

        /* ============================================================
           1) REGISTRO CLIENTE EXPLÍCITO
        ============================================================ */
        if (flow === 'register-client') {
          const perfiles = await getPerfilesUsuario(user.id);
          const esCliente =
            perfiles?.some((p: any) => p.perfil === 'cliente') ?? false;

          if (esCliente) {
            const returnTo =
              localStorage.getItem(LS.afterRegisterReturnTo) ||
              localStorage.getItem(LS.clientBookingReturnUrl);

            if (returnTo) {
              localStorage.removeItem(LS.afterRegisterReturnTo);
              localStorage.removeItem(LS.clientBookingReturnUrl);
              navigate(returnTo, { replace: true });
              return;
            }

            const perfilAuto = perfiles?.find(
              (p) => p.inicio_automatico === true
            );
            const perfilFinal = perfilAuto ?? perfiles?.[0];

            if (perfilFinal) {
              localStorage.setItem(LS.idPerfil, perfilFinal.id_perfil || '');
              localStorage.setItem(LS.idEmpresa, perfilFinal.id_empresa || '');
              navigate(rutaInicioPanel(perfilFinal.ruta_panel), {
                replace: true,
              });
              return;
            }

            navigate('/profiles', { replace: true });
            return;
          }

          // Ya tuvo perfil de cliente y lo dio de baja: no tiene sentido
          // hacerle rellenar el registro otra vez. Se le ofrece reactivarlo.
          const bajaCliente = await getPerfilClienteDesactivado(user.id);

          if (bajaCliente) {
            navigate('/reactivar-perfil', { replace: true });
            return;
          }

          const returnUrl = localStorage.getItem(LS.clientBookingReturnUrl) || '/panel-cliente';
          setGooglePrefill(user);
          localStorage.setItem(LS.afterRegisterReturnTo, returnUrl);
          navigate('/register-client', { replace: true });
          return;
        }

        /* ============================================================
           1.b) REGISTRO EMPRESA EXPLÍCITO
        ============================================================ */
        if (flow === 'register-company') {
          const perfiles = await getPerfilesUsuario(user.id);
          const esEmpresa =
            perfiles?.some((p: any) => p.perfil === 'empresa') ?? false;

          if (esEmpresa) {
            const perfilAuto = perfiles?.find(
              (p: any) => p.inicio_automatico === true
            );
            const perfilFinal = perfilAuto ?? perfiles?.[0];

            if (perfilFinal) {
              localStorage.setItem(LS.idPerfil, perfilFinal.id_perfil || '');
              localStorage.setItem(LS.idEmpresa, perfilFinal.id_empresa || '');
              navigate(rutaInicioPanel(perfilFinal.ruta_panel), {
                replace: true,
              });
              return;
            }

            navigate('/profiles', { replace: true });
            return;
          }

          setGooglePrefill(user);
          localStorage.setItem(LS.oauthContext, 'companyRegister');
          navigate('/register-company', { replace: true });
          return;
        }

        /* ============================================================
           2) CLIENT BOOKING
        ============================================================ */
        if (isClientBooking) {
          const esCliente = await hasPerfil('cliente');

          const returnUrl =
            returnToParam ||
            localStorage.getItem(LS.clientBookingReturnUrl) ||
            '/';

          if (!esCliente) {
            // Cliente dado de baja que vuelve a reservar: recuperar el perfil
            // es un clic, rellenar el registro entero no.
            const bajaCliente = await getPerfilClienteDesactivado(user.id);

            if (bajaCliente) {
              localStorage.setItem(LS.afterRegisterReturnTo, returnUrl);
              clearBookingReturn();
              navigate('/reactivar-perfil', { replace: true });
              return;
            }

            localStorage.setItem(LS.afterRegisterReturnTo, returnUrl);
            setGooglePrefill(user);
            clearBookingReturn();
            navigate('/register-client', { replace: true });
            return;
          }

          clearBookingReturn();
          navigate(returnUrl, { replace: true });
          return;
        }

        /* ============================================================
           3) LOGIN NORMAL
        ============================================================ */
        const perfiles = await getPerfilesUsuario(user.id);
        const total = perfiles?.length ?? 0;

        if (total === 0) {
          // Sin perfiles activos puede ser un usuario nuevo (elige tipo de
          // cuenta) o uno que se dio de baja (recupera el suyo). Preguntarle
          // "¿cómo quieres usar Bookoll?" al segundo no tiene sentido.
          const bajaCliente = await getPerfilClienteDesactivado(user.id);

          if (bajaCliente) {
            navigate('/profiles', { replace: true });
            return;
          }

          setGooglePrefill(user);
          navigate('/choose-profile', { replace: true });
          return;
        }

        const perfilAuto = perfiles.find(
          (p: any) => p.inicio_automatico === true
        );

        // Si hay perfil automático, ir directo a él
        if (perfilAuto) {
          localStorage.setItem(LS.idPerfil, perfilAuto.id_perfil || '');
          localStorage.setItem(LS.idEmpresa, perfilAuto.id_empresa || '');
          navigate(
            rutaInicioPanel(perfilAuto.ruta_panel),
            { replace: true }
          );
          return;
        }

        // Un solo perfil: ir directo
        if (perfiles.length === 1) {
          const p = perfiles[0];
          localStorage.setItem(LS.idPerfil, p.id_perfil || '');
          localStorage.setItem(LS.idEmpresa, p.id_empresa || '');
          navigate(p.ruta_panel ? `/${p.ruta_panel}` : '/', { replace: true });
          return;
        }

        // Varios perfiles sin automático: dejar que el usuario elija
        navigate('/profiles', { replace: true });
        return;
      } catch (err) {
        Sentry.captureException(err);
        toast.error('Error inesperado');
        navigate('/', { replace: true });
      }
    };

    run();
  }, [navigate, hasPerfil, getPerfilesUsuario, getPerfilClienteDesactivado]);

  // Mismo lenguaje visual que Login/PrivateRoute: gradiente + logo,
  // barra pulsando en opacidad (nunca se ve "a medias" si loading se
  // corta a mitad de la animación).
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-6 px-6 overflow-hidden">
      <div
        className="absolute inset-0 dark:hidden"
        style={{
          background:
            'linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)',
        }}
      />
      <div
        className="absolute inset-0 hidden dark:block"
        style={{
          background:
            'linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)',
        }}
      />

      <motion.img
        src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
        alt="Bookoll"
        className="relative z-10 h-12"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      />

      <motion.p
        className="relative z-10 text-sm text-white/70"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        Completando inicio de sesión…
      </motion.p>

      <div className="relative z-10 w-full max-w-xs h-1.5 rounded-full bg-white/15 overflow-hidden">
        <motion.div
          className="h-full w-full rounded-full"
          style={{
            background: 'linear-gradient(90deg, #288E7F 0%, #34a894 100%)',
          }}
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
    </div>
  );
};

export default OAuthCallback;